import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './InfoPages.css'

function generateSlug(name) {
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 40)
}

function getBlockText(text, lang) {
  if (!text) return ''
  if (typeof text === 'string') return text
  return text[lang] || text['de'] || Object.values(text)[0] || ''
}

const LANGUAGES = [
  ['de','Deutsch'],['en','English'],['es','Español'],['it','Italiano'],
  ['pl','Polski'],['nl','Nederlands'],['da','Dansk'],['fr','Français'],
  ['hu','Magyar'],['hr','Hrvatski'],['pt','Português'],['cs','Čeština'],
  ['bg','Български'],['et','Eesti'],['fi','Suomi'],['el','Ελληνικά'],
  ['lv','Latviešu'],['lt','Lietuvių'],['no','Norsk'],['ru','Русский'],
  ['sr','Srpski'],['sk','Slovenčina'],['sl','Slovenščina'],['sv','Svenska'],
  ['tr','Türkçe'],['uk','Українська'],
]

function generateId() {
  return Math.random().toString(36).slice(2, 10)
}

// ── Block Editor ──────────────────────────────────────────────────────────────
function BlockEditor({ blocks, onChange, sourceLang, apiFetch, tenantId }) {
  const dragIdx = useRef(null)
  const [uploadingIdx, setUploadingIdx] = useState(null)
  // Only the video upload surfaces errors: it has a size ceiling and a format list to
  // fall foul of, where an image upload either works or is a storage problem.
  const [uploadError, setUploadError] = useState(null)

  const updateBlock = (idx, patch) => {
    onChange(blocks.map((b, i) => i === idx ? { ...b, ...patch } : b))
  }

  const removeBlock = (idx) => onChange(blocks.filter((_, i) => i !== idx))

  const addBlock = (type) => {
    onChange([...blocks, { id: generateId(), type, text: '', ...(type === 'image' ? { url: '', caption: '' } : {}) }])
  }

  const handleDragStart = (e, idx) => { dragIdx.current = idx; e.dataTransfer.effectAllowed = 'move' }
  const handleDragOver = (e, idx) => {
    e.preventDefault()
    if (dragIdx.current === null || dragIdx.current === idx) return
    const next = [...blocks]
    const [dragged] = next.splice(dragIdx.current, 1)
    next.splice(idx, 0, dragged)
    dragIdx.current = idx
    onChange(next)
  }
  const handleDrop = () => { dragIdx.current = null }

  const handleImageUpload = async (e, idx) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingIdx(idx)
    const reader = new FileReader()
    reader.onload = async (ev) => {
      const base64 = ev.target.result.split(',')[1]
      try {
        const res = await apiFetch('/api/admin/upload-icon', {
          method: 'POST',
          body: JSON.stringify({ tenantId, fileName: file.name, fileData: base64 }),
        })
        const data = await res.json()
        if (res.ok) updateBlock(idx, { url: data.url })
      } catch {} finally {
        setUploadingIdx(null)
        e.target.value = ''
      }
    }
    reader.readAsDataURL(file)
  }

  /**
   * Videos go to their own endpoint, which enforces the format list and the size
   * ceiling. Unlike the image upload this surfaces the server's error: a rejected
   * 80 MB file has to say so, or the admin just sees nothing happen and tries again.
   */
  const handleVideoUpload = async (e, idx) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Checked here as well as on the server, which stays the authority. Without it the
    // browser base64-encodes the whole file — a third larger again — and holds it in
    // memory before the server gets a chance to refuse, which on a big file means a
    // long wait and then an error. Keep in step with MAX_BYTES in the route.
    const MAX_MB = 50
    if (file.size > MAX_MB * 1024 * 1024) {
      setUploadError(`Video ist ${(file.size / 1024 / 1024).toFixed(0)} MB groß. Maximal ${MAX_MB} MB.`)
      e.target.value = ''
      return
    }

    setUploadingIdx(idx)
    setUploadError(null)
    const reader = new FileReader()
    reader.onload = async (ev) => {
      const base64 = ev.target.result.split(',')[1]
      try {
        const res = await apiFetch('/api/admin/upload-video', {
          method: 'POST',
          body: JSON.stringify({ tenantId, fileName: file.name, fileData: base64 }),
        })
        const data = await res.json().catch(() => ({}))
        if (res.ok) updateBlock(idx, { url: data.url })
        else setUploadError(data.error || 'Upload fehlgeschlagen.')
      } catch {
        setUploadError('Upload fehlgeschlagen.')
      } finally {
        setUploadingIdx(null)
        e.target.value = ''
      }
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="ip-block-editor">
      {uploadError && <div className="alert alert-error">{uploadError}</div>}
      <div className="ip-block-list">
        {blocks.map((block, idx) => (
          <div
            key={block.id || idx}
            className="ip-block-row"
            draggable
            onDragStart={e => handleDragStart(e, idx)}
            onDragOver={e => handleDragOver(e, idx)}
            onDrop={handleDrop}
          >
            <span className="ip-drag-handle">≡</span>
            <div className="ip-block-body">
              <div className="ip-block-header">
                <span className={`ip-block-type-badge ip-block-type-badge--${block.type}`}>
                  {block.type === 'heading' ? 'H'
                    : block.type === 'paragraph' ? 'P'
                    : block.type === 'video' ? '▶' : '🖼'}
                </span>
                {block.type !== 'image' && block.type !== 'video' && (
                  <textarea
                    className="form-control ip-block-textarea"
                    rows={block.type === 'heading' ? 1 : 3}
                    value={getBlockText(block.text, sourceLang)}
                    onChange={e => updateBlock(idx, { text: e.target.value })}
                    placeholder={block.type === 'heading' ? 'Überschrift...' : 'Text...'}
                  />
                )}
                {block.type === 'image' && (
                  <div className="ip-image-block">
                    <div className="ip-image-url-row">
                      <input
                        className="form-control"
                        value={block.url || ''}
                        onChange={e => updateBlock(idx, { url: e.target.value })}
                        placeholder="Bild-URL"
                      />
                      <label className="btn btn-ghost btn-sm">
                        {uploadingIdx === idx ? 'Hochladen…' : '📁 Hochladen'}
                        <input type="file" accept="image/*" className="ip-file-input"
                          onChange={e => handleImageUpload(e, idx)} disabled={uploadingIdx === idx} />
                      </label>
                    </div>
                    {block.url && (
                      <img src={block.url} alt="" className="ip-image-preview" />
                    )}
                    <input
                      className="form-control"
                      value={getBlockText(block.caption, sourceLang)}
                      onChange={e => updateBlock(idx, { caption: e.target.value })}
                      placeholder="Bildunterschrift (optional)"
                    />
                  </div>
                )}
                {block.type === 'video' && (
                  <div className="ip-image-block">
                    <div className="ip-image-url-row">
                      <input
                        className="form-control"
                        value={block.url || ''}
                        onChange={e => updateBlock(idx, { url: e.target.value })}
                        placeholder="Video-URL"
                      />
                      <label className="btn btn-ghost btn-sm">
                        {uploadingIdx === idx ? 'Hochladen…' : '📁 Hochladen'}
                        <input type="file" accept="video/mp4,video/webm" className="ip-file-input"
                          onChange={e => handleVideoUpload(e, idx)} disabled={uploadingIdx === idx} />
                      </label>
                    </div>
                    {block.url && (
                      <video src={block.url} controls className="ip-video-preview" preload="metadata" />
                    )}
                    <input
                      className="form-control"
                      value={getBlockText(block.caption, sourceLang)}
                      onChange={e => updateBlock(idx, { caption: e.target.value })}
                      placeholder="Beschriftung (optional)"
                    />
                    {/* Per block rather than per page: a page can hold a mandatory
                        safety briefing and an optional wayfinding clip at once. */}
                    <label className="ip-video-required">
                      <input
                        type="checkbox"
                        checked={block.required ?? false}
                        onChange={e => updateBlock(idx, { required: e.target.checked })}
                      />
                      Muss vollständig angesehen werden
                    </label>
                    <span className="ip-video-hint">
                      MP4 oder WebM, maximal 50 MB. Große Dateien laden am Terminal
                      langsam — vorher komprimieren.
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button className="ffb-btn-icon ffb-btn-icon--danger" onClick={() => removeBlock(idx)}>✕</button>
          </div>
        ))}
        {blocks.length === 0 && (
          <p className="ip-empty-hint">Noch keine Blöcke — füge den ersten Block hinzu.</p>
        )}
      </div>

      <div className="ip-add-block-row">
        <button className="btn btn-ghost btn-sm" onClick={() => addBlock('heading')}>+ Überschrift</button>
        <button className="btn btn-ghost btn-sm" onClick={() => addBlock('paragraph')}>+ Absatz</button>
        <button className="btn btn-ghost btn-sm" onClick={() => addBlock('image')}>+ Bild</button>
        <button className="btn btn-ghost btn-sm" onClick={() => addBlock('video')}>+ Video</button>
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function InfoPages() {
  const { apiFetch, isOwner, admin } = useAuth()

  const [tenants,        setTenants]        = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [pages,          setPages]          = useState([])
  const [selectedPage,   setSelectedPage]   = useState(null)  // null = new
  // pageId -> [{ flowType, stepId, via }]. null means the lookup failed, which is
  // deliberately distinct from {} (looked up fine, nothing uses anything).
  const [usage,          setUsage]          = useState({})
  const [loading,        setLoading]        = useState(false)
  const [saving,         setSaving]         = useState(false)
  const [error,          setError]          = useState(null)
  const [success,        setSuccess]        = useState(false)

  // Editor state
  const [name,       setName]       = useState('')
  const [slug,       setSlug]       = useState('')
  const [sourceLang, setSourceLang] = useState('de')
  const [blocks,     setBlocks]     = useState([])
  const [slugTouched, setSlugTouched] = useState(false)

  // Load tenants
  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants').then(r => r.json()).then(data => {
        const list = Array.isArray(data) ? data : []
        setTenants(list)
        if (list.length > 0) setSelectedTenant(list[0].id)
      }).catch(() => {})
    } else {
      setSelectedTenant(admin.tenant_id)
      setTenants([{ id: admin.tenant_id, name: admin.email }])
    }
  }, [isOwner])

  // Load pages when tenant changes, together with where each one is used.
  // Usage is a separate request so the Flow Builder's own page picker — which only
  // needs names — is unaffected. A usage failure must not hide the pages themselves,
  // so it degrades to "unknown" rather than an error.
  const loadPages = () => {
    if (!selectedTenant) return
    setLoading(true)
    apiFetch(`/api/admin/info-pages/${selectedTenant}`)
      .then(r => r.json())
      .then(data => { setPages(Array.isArray(data) ? data : []); setLoading(false) })
      .catch(() => setLoading(false))

    apiFetch(`/api/admin/info-pages/${selectedTenant}/usage`)
      .then(r => r.ok ? r.json() : {})
      .then(data => setUsage(data && typeof data === 'object' ? data : {}))
      .catch(() => setUsage(null))
  }

  useEffect(loadPages, [selectedTenant])

  // How a page is reached, in admin-facing wording. A destination reference has no
  // flow step behind it — the ramp carries the page — so it names the ramp instead.
  const describeRef = (ref) =>
    ref.via === 'destination' ? `Zielort „${ref.destinationName || '?'}"`
    : ref.via === 'rule'      ? `${ref.flowType} — Regel einer dynamischen Seite`
    : ref.via === 'fallback'  ? `${ref.flowType} — Rückfallseite einer dynamischen Seite`
    : ref.via === 'manual'    ? `${ref.flowType} — auswählbar bei manueller Zuweisung`
    : `${ref.flowType} — Informationsseiten-Schritt`

  // "in 2 Schritten verwendet" was wrong for a page a ramp carries: there is no step.
  // Counted separately so the label says which kind of use it is, since that decides
  // where to go and change it.
  const usageLabel = (refs) => {
    const dests = refs.filter(r => r.via === 'destination').length
    const steps = refs.length - dests
    if (dests && steps) return `an ${refs.length} Stellen verwendet`
    if (dests) return dests === 1 ? 'von 1 Zielort verwendet' : `von ${dests} Zielorten verwendet`
    return steps === 1 ? 'in 1 Schritt verwendet' : `in ${steps} Schritten verwendet`
  }

  const openNew = () => {
    setSelectedPage(null)
    setName(''); setSlug(''); setBlocks([]); setSlugTouched(false)
    setError(null); setSuccess(false)
  }

  const openPage = (page) => {
    setSelectedPage(page)
    setName(page.name)
    setSlug(page.slug)
    setBlocks((page.blocks || []).map((b, i) => ({ ...b, id: b.id || String(i) })))
    setSlugTouched(true)
    setError(null); setSuccess(false)
  }

  const handleNameChange = (val) => {
    setName(val)
    if (!slugTouched) setSlug(generateSlug(val))
  }

  const handleSave = async () => {
    if (!name.trim() || !slug.trim()) { setError('Name und Slug sind erforderlich'); return }
    setSaving(true); setError(null); setSuccess(false)
    try {
      const body = { name: name.trim(), slug: slug.trim(), sourceLang, blocks }
      const url = selectedPage
        ? `/api/admin/info-pages/${selectedTenant}/${selectedPage.id}`
        : `/api/admin/info-pages/${selectedTenant}`
      const res = await apiFetch(url, { method: selectedPage ? 'PUT' : 'POST', body: JSON.stringify(body) })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Fehler beim Speichern')
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      setSelectedPage(data)
      setPages(prev => selectedPage
        ? prev.map(p => p.id === data.id ? data : p)
        : [...prev, data].sort((a, b) => a.name.localeCompare(b.name))
      )
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (page) => {
    if (!window.confirm(`"${page.name}" löschen?`)) return

    const url = `/api/admin/info-pages/${selectedTenant}/${page.id}`
    let res = await apiFetch(url, { method: 'DELETE' })

    // 409: still referenced by a flow. The server tells us where, so the admin can
    // make an informed choice instead of discovering it later at a kiosk.
    if (res.status === 409) {
      let refs = []
      try { refs = (await res.json()).usage || [] } catch { /* keep refs empty */ }

      const where = refs.map(r => `• ${describeRef(r)}`).join('\n')
      const usedByDestination = refs.some(r => r.via === 'destination')
      const proceed = window.confirm(
        `"${page.name}" wird noch verwendet:\n\n${where}\n\n` +
        `Beim Löschen zeigen diese Schritte die Rückfallseite bzw. einen Hinweis ` +
        `"Bitte wenden Sie sich an den Empfang".` +
        // Spelled out separately: a ramp keeps working, but the content somebody wrote
        // for it is replaced by the generated "Ihr Ziel: Rampe 4" placeholder.
        (usedByDestination
          ? `\n\nBei einem Zielort wird stattdessen nur noch der Name des Zielorts angezeigt.`
          : '') +
        `\n\nTrotzdem löschen?`
      )
      if (!proceed) return

      res = await apiFetch(`${url}?force=true`, { method: 'DELETE' })
    }

    if (!res.ok) {
      setError('Seite konnte nicht gelöscht werden.')
      return
    }

    setPages(prev => prev.filter(p => p.id !== page.id))
    setUsage(prev => {
      if (!prev) return prev
      const next = { ...prev }
      delete next[page.id]
      return next
    })
    if (selectedPage?.id === page.id) openNew()
  }

  const isEditing = selectedPage !== null

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Informationsseiten</h1>
          <p>Konfigurierbare Seiten mit Text und Bildern für Besucher-Flows</p>
        </div>
      </div>

      {isOwner && tenants.length > 0 && (
        <div className="card ip-tenant-card">
          <div className="form-group ip-tenant-form-group">
            <label>Mandant</label>
            <select className="form-control" value={selectedTenant} onChange={e => { setSelectedTenant(e.target.value); openNew() }}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      )}

      <div className="ip-layout">
        {/* ── Left panel: page list ── */}
        <div className="ip-sidebar">
          <div className="ip-sidebar-header">
            <span className="section-title">Seiten</span>
            {/* Creation lives here, not only in the Flow Builder: a dynamic
                information page only ever references pages, so it cannot create the
                ones its rules need. */}
            <button
              className="btn btn-ghost btn-sm ip-new-btn"
              onClick={openNew}
              disabled={!selectedTenant}
              title="Neue Informationsseite anlegen"
            >+ Neue Seite</button>
          </div>
          {loading ? (
            <div className="loading-state"><span className="spinner" /></div>
          ) : (
            <div className="ip-page-list">
              {pages.map(page => (
                <div
                  key={page.id}
                  className={`ip-page-item${selectedPage?.id === page.id ? ' active' : ''}`}
                  onClick={() => openPage(page)}
                >
                  <div className="ip-page-item-name">{page.name}</div>
                  <div className="ip-page-item-slug">{page.slug}</div>
                  {/* Usage, so an unreferenced page is visibly unreferenced rather
                      than indistinguishable from a live one. */}
                  {usage === null ? null : (
                    (usage[page.id]?.length ?? 0) === 0 ? (
                      <div className="ip-page-item-usage ip-page-item-usage--unused">
                        nicht verwendet
                      </div>
                    ) : (
                      <div
                        className="ip-page-item-usage"
                        title={usage[page.id].map(describeRef).join('\n')}
                      >
                        {usageLabel(usage[page.id])}
                      </div>
                    )
                  )}
                  <button
                    className="ffb-btn-icon ffb-btn-icon--danger ip-delete-btn"
                    onClick={e => { e.stopPropagation(); handleDelete(page) }}
                    title="Löschen"
                  >🗑</button>
                </div>
              ))}
              {pages.length === 0 && !loading && (
                <p className="ip-empty-hint">Noch keine Seiten. Lege mit „+ Neue Seite" die erste an.</p>
              )}
            </div>
          )}
        </div>

        {/* ── Right panel: editor ── */}
        <div className="card ip-editor">
          <div className="section-title ip-editor-title">
            {/* Previously "Seite auswählen", which hid the fact that the empty form
                IS the create form. */}
            {isEditing ? `Bearbeiten: ${selectedPage.name}` : 'Neue Informationsseite'}
          </div>

          {error   && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">✓ Gespeichert & übersetzt</div>}

          <div className="ip-meta-row">
            <div className="form-group ip-form-group-flex">
              <label>Name <span className="ip-hint">(interner Name)</span></label>
              <input className="form-control" value={name} onChange={e => handleNameChange(e.target.value)} placeholder="z. B. Willkommen" />
            </div>
            <div className="form-group">
              <label>Eingabesprache</label>
              <select className="form-control" value={sourceLang} onChange={e => setSourceLang(e.target.value)}>
                {LANGUAGES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </div>
          </div>

          <BlockEditor
            blocks={blocks}
            onChange={setBlocks}
            sourceLang={sourceLang}
            apiFetch={apiFetch}
            tenantId={selectedTenant}
          />

          <div className="ip-save-area">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner spinner-sm" /> Übersetzen & Speichern…</> : 'Speichern & Übersetzen'}
            </button>
          </div>
        </div>
      </div>

      {selectedTenant && (
        <VersionHistoryPanel table="info_pages" tenantId={selectedTenant} onRestored={loadPages} />
      )}
    </div>
  )
}
